import { NavProvider, useNav } from "@/utils/nav";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type RoutePaths, Router, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";
import { colors, icons } from "@patient-watch/icons";
import { initI18n, i18nInstance, I18nextProvider, readStoredLocale, resolveLocale } from "@patient-watch/i18n";
import { LocaleProvider } from "@/features/i18n/LocaleProvider";
import { ThemeProvider } from "./hooks/useTheme";
import { routeTree } from "./routeTree.gen";
import { AuthProvider, roles, useAuth } from "./utils/auth";
import "./lib/sentry"; // Initialize Sentry for error tracking
import { initializeSentryRouter } from "./lib/sentry";
import { installStaleChunkReload } from "./lib/staleChunkReload";
import { APP_SCROLL_CONTAINER_ID } from "@/services/scroll";

installStaleChunkReload();

const queryClient = new QueryClient();

const bootstrapLocale = resolveLocale({
    storedLocale: readStoredLocale(),
    navigatorLanguage: typeof navigator !== "undefined" ? navigator.language : null,
});

// Set up a Router instance
export const router = new Router({
    routeTree,
    defaultPendingComponent: () => (
        <div className="p-2 text-2xl">
            <icons.JazzySpinner fill={colors["indigo-200"]} />
        </div>
    ),
    context: {
        queryClient,
        auth: {
            isAuthenticated: false,
            isAuthLoading: true,
            login: () => {},
            logout: () => {},
            id: "",
            roles,
            setRoles: () => {},
            isSuperUser: false,
            setIsSuperUser: () => {},
        },
        nav: {
            backRoute: { label: "Home", route: { to: "/" } },
            breadcrumbRoutes: [{ label: "Home", route: { to: "/" } }],
            setNav: () => {},
            reset: () => {},
        },
        dark: false,
    },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    // The app scrolls `#app-scroll-container`, not `window`. Without this, page
    // changes restore the previous offset instead of returning to the top.
    scrollToTopSelectors: [`#${APP_SCROLL_CONTAINER_ID}`],
});

// Initialize Sentry router integration
initializeSentryRouter(router);

export type ValidRoutes = RoutePaths<typeof routeTree>;

// Register things for typesafety
declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const rootElement = document.getElementById("app");
if (!rootElement) {
    throw new Error("Root element not found");
}

function InnerApp() {
    const auth = useAuth();
    const nav = useNav();

    return <RouterProvider router={router} context={{ auth, nav }} />;
}

if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    void initI18n(bootstrapLocale).then(() => {
        root.render(
            <QueryClientProvider client={queryClient}>
                <I18nextProvider i18n={i18nInstance}>
                    <AuthProvider>
                        <LocaleProvider>
                            <NavProvider>
                                <ThemeProvider>
                                    <InnerApp />
                                </ThemeProvider>
                            </NavProvider>
                        </LocaleProvider>
                    </AuthProvider>
                </I18nextProvider>
            </QueryClientProvider>
        );
    });
}
